import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Boxes, Clock3, ExternalLink, FileText, Loader2, Sparkles, Workflow } from 'lucide-react'
import ExecutionPanel from '@components/operate/ExecutionPanel'
import { Badge } from '@components/ui/Badge'
import { Button } from '@components/ui/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@components/ui/Dialog'
import {
  runInsetCardClassName,
  runMutedPanelClassName,
} from '@/components/operate/RunChrome'
import { workspaceContextAPI, type WorkspaceContext } from '@/services/api/workspaceContext'
import {
  recipesAPI,
  type RecipeExecutionStatus,
} from '@/services/api/recipes'
import {
  skillsAPI,
  type DeploymentSkillDocument,
  type DeploymentSkillSummary,
} from '@/services/api/skills'
import { getStorageApiUrl } from '@/config/runtime'
import { useContextStore } from '@/store/context'
import { useOperateStore } from '@/store/operate'
import type { ChatExecutionPanelTarget, ExecutionStep } from '@/store/operate'
import type { MetaRecette } from '@/types/recipe'
import {
  isExecutionExpiredError,
  isExecutionPayloadExpired,
  sanitizeExecutionDetailError,
} from '@/services/chat/executionDetailView'
import RecipePreviewCanvas from './RecipePreviewCanvas'
import RecipeExecutionResultPanel from './RecipeExecutionResultPanel'
import RecipeExecutionLogsPanel from './RecipeExecutionLogsPanel'

interface RunContextPanelProps {
  sessionId?: string | null
  recipeId?: string | null
  recipePreview?: MetaRecette | null
  executionSteps: ExecutionStep[]
  executionPanelOpen: boolean
  onToggleExecution: () => void
  executionDetailTarget?: ChatExecutionPanelTarget | null
  onCloseExecutionDetail?: () => void
  totalDuration?: number
  currentProgress?: number
  showExecutionPanel?: boolean
}

function renderValue(value: unknown) {
  if (value === null || value === undefined || value === '') return 'N/A'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function findValue(row: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    if (key in row) return row[key]
  }
  return undefined
}

function findTextPreview(row: Record<string, unknown>) {
  const candidate = findValue(row, ['preview', 'body', 'content', 'text', 'summary'])
  return typeof candidate === 'string' && candidate.trim() ? candidate : null
}

function buildArtifactLink(row: Record<string, unknown>, deploymentId?: string | null) {
  const directUrl = findValue(row, ['url', 'download_url', 'storage_uri'])
  if (typeof directUrl === 'string' && /^https?:\/\//.test(directUrl)) {
    return directUrl
  }

  const path = findValue(row, ['path', 'storage_path'])
  const storageUrl = getStorageApiUrl().replace(/\/+$/, '')
  if (typeof path === 'string' && path.startsWith('/') && deploymentId && storageUrl) {
    return `${storageUrl}/api/v1/file/link/${deploymentId}${path}`
  }

  return null
}

function SummaryCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Clock3
  label: string
  value: string | number
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" aria-hidden="true" />
        {label}
      </div>
      <div className="font-semibold text-foreground">{value}</div>
    </div>
  )
}

function ContextList({
  title,
  icon: Icon,
  rows,
  emptyLabel,
  summaryKeys,
  onPreviewRow,
}: {
  title: string
  icon: typeof Clock3
  rows: Array<Record<string, unknown>>
  emptyLabel: string
  summaryKeys: string[]
  onPreviewRow?: (row: Record<string, unknown>) => void
}) {
  const { t } = useTranslation()
  return (
    <section className={runInsetCardClassName}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyLabel}</p>
      ) : (
        <div className="mt-3 space-y-2">
          {rows.slice(0, 5).map((row, index) => {
            const main = renderValue(findValue(row, summaryKeys))
            const secondary = renderValue(
              findValue(row, ['summary', 'status', 'kind', 'name', 'title']),
            )
            return (
              <div
                key={index}
                className="rounded-xl border border-border bg-card px-3 py-2"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{main}</div>
                    {secondary !== 'N/A' && (
                      <div className="mt-1 text-xs text-muted-foreground">{secondary}</div>
                    )}
                  </div>
                  {onPreviewRow && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => onPreviewRow(row)}>
                      {t('common.open')}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default function RunContextPanel({
  sessionId,
  recipeId,
  recipePreview = null,
  executionSteps,
  executionPanelOpen,
  onToggleExecution,
  executionDetailTarget,
  onCloseExecutionDetail,
  totalDuration = 0,
  currentProgress = 0,
  showExecutionPanel = true,
}: RunContextPanelProps) {
  const { t } = useTranslation()
  const orgId = useContextStore((state) => state.org)
  const deploymentId = useContextStore((state) => state.deploymentId)
  const selectedSkillIds = useOperateStore((state) => state.selectedSkillIds)
  const selectedSkillDetails = useOperateStore((state) => state.selectedSkillDetails)
  const toggleSelectedSkillId = useOperateStore((state) => state.toggleSelectedSkillId)
  const hydrateSelectedSkillDetail = useOperateStore((state) => state.hydrateSelectedSkillDetail)
  const messages = useOperateStore((state) => state.messages)
  const [workspaceContext, setWorkspaceContext] = useState<WorkspaceContext | null>(null)
  const [recipe, setRecipe] = useState<MetaRecette | null>(null)
  const [deploymentSkills, setDeploymentSkills] = useState<DeploymentSkillSummary[]>([])
  const [skillsLoading, setSkillsLoading] = useState(false)
  const [skillsError, setSkillsError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [recipeLoading, setRecipeLoading] = useState(false)
  const [recipeError, setRecipeError] = useState<string | null>(null)
  const [previewArtifact, setPreviewArtifact] = useState<{ title: string; body: string; link?: string | null } | null>(null)
  const [detailExecution, setDetailExecution] = useState<RecipeExecutionStatus | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [detailExpired, setDetailExpired] = useState(false)
  const [detailRefreshTick, setDetailRefreshTick] = useState(0)

  useEffect(() => {
    if (recipeId) {
      setRecipeLoading(true)
      setRecipeError(null)
      return
    }

    setRecipe(null)
    setRecipeError(null)
    setRecipeLoading(false)
  }, [recipeId, sessionId])

  useEffect(() => {
    let cancelled = false

    const loadContext = async () => {
      if (!sessionId) {
        setWorkspaceContext(null)
        setError(null)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        const context = await workspaceContextAPI.getSessionWorkspaceContext(sessionId, 12)
        if (!cancelled) {
          setWorkspaceContext(context)
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : t('build.loadingContext'))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadContext()
    if (!sessionId) {
      return () => {
        cancelled = true
      }
    }

    const interval = window.setInterval(() => {
      void loadContext()
    }, 5000)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [sessionId])

  useEffect(() => {
    let cancelled = false

    const loadRecipe = async () => {
      if (recipePreview && (!recipeId || recipePreview.id === recipeId)) {
        setRecipe(recipePreview)
        setRecipeError(null)
        setRecipeLoading(false)
        return
      }

      if (!recipeId || !orgId || !deploymentId) {
        setRecipe(null)
        setRecipeError(null)
        setRecipeLoading(false)
        return
      }

      try {
        setRecipeLoading(true)
        setRecipeError(null)
        const loadedRecipe = await recipesAPI.getRecipe(orgId, deploymentId, recipeId)
        if (!cancelled) {
          setRecipe(loadedRecipe)
        }
      } catch (loadError) {
        if (!cancelled) {
          setRecipe(null)
          setRecipeError(
            loadError instanceof Error ? loadError.message : t('build.loadingRecipePreview'),
          )
        }
      } finally {
        if (!cancelled) {
          setRecipeLoading(false)
        }
      }
    }

    void loadRecipe()

    return () => {
      cancelled = true
    }
  }, [deploymentId, orgId, recipeId, recipePreview])

  useEffect(() => {
    let cancelled = false

    const loadSkills = async () => {
      if (!orgId || !deploymentId) {
        setDeploymentSkills([])
        setSkillsError(null)
        setSkillsLoading(false)
        return
      }

      try {
        setSkillsLoading(true)
        setSkillsError(null)
        const skills = await skillsAPI.listDeploymentSkills(orgId, deploymentId)
        if (!cancelled) {
          setDeploymentSkills(skills)
        }
      } catch (loadError) {
        if (!cancelled) {
          setDeploymentSkills([])
          setSkillsError(
            loadError instanceof Error ? loadError.message : t('chat.skillsLoadingError'),
          )
        }
      } finally {
        if (!cancelled) {
          setSkillsLoading(false)
        }
      }
    }

    void loadSkills()
    return () => {
      cancelled = true
    }
  }, [deploymentId, orgId, t])

  useEffect(() => {
    let cancelled = false

    const loadExecutionDetail = async () => {
      if (!executionDetailTarget || !orgId || !deploymentId) {
        setDetailExecution(null)
        setDetailError(null)
        setDetailExpired(false)
        setDetailLoading(false)
        return
      }

      try {
        setDetailLoading(true)
        setDetailError(null)
        setDetailExpired(false)
        const execution = await recipesAPI.getScopedRecipeExecutionStatus(
          orgId,
          deploymentId,
          executionDetailTarget.executionId,
          {
            include_logs: executionDetailTarget.mode === 'logs',
          },
        )
        if (!cancelled) {
          setDetailExecution(execution)
          setDetailExpired(isExecutionPayloadExpired(execution.payload_state))
        }
      } catch (loadError) {
        if (!cancelled) {
          setDetailExecution(null)
          const expired = isExecutionExpiredError(loadError)
          setDetailExpired(expired)
          const hasUserFacingContent = Boolean(
            executionDetailTarget.resultData ||
              executionDetailTarget.logs?.length ||
              executionDetailTarget.messageContent?.trim(),
          )
          setDetailError(
            expired || hasUserFacingContent
              ? null
              : sanitizeExecutionDetailError(
                  loadError,
                  executionDetailTarget.mode === 'logs'
                    ? t('executionLogs.empty')
                    : t('executionResult.empty'),
                ),
          )
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false)
        }
      }
    }

    void loadExecutionDetail()

    return () => {
      cancelled = true
    }
  }, [deploymentId, detailRefreshTick, executionDetailTarget, orgId])

  const counts = useMemo(
    () => ({
      tasks: workspaceContext?.openTasksCount ?? workspaceContext?.openTasks.length ?? 0,
      timeline: workspaceContext?.timelineCount ?? workspaceContext?.timeline.length ?? 0,
      artifacts:
        workspaceContext?.artifactsCount ?? workspaceContext?.recentArtifacts.length ?? 0,
      sources:
        workspaceContext?.sourceGraphCount ?? workspaceContext?.sourceGraph.length ?? 0,
    }),
    [workspaceContext],
  )

  const openArtifactPreview = (row: Record<string, unknown>) => {
    const title = renderValue(findValue(row, ['name', 'artifact_id', 'title', 'path']))
    const body = findTextPreview(row)
    const link = buildArtifactLink(row, deploymentId)

    if (body) {
      setPreviewArtifact({ title, body, link })
      return
    }

    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer')
    }
  }

  const selectedSkills = useMemo(
    () =>
      selectedSkillIds
        .map((skillId) => {
          const detail = selectedSkillDetails[skillId]
          if (detail) {
            return detail
          }
          const summary = deploymentSkills.find((item) => item.id === skillId)
          if (!summary) return null
          return {
            ...summary,
            content: '',
            body: '',
            frontmatter: {},
            files: [],
          } satisfies DeploymentSkillDocument
        })
        .filter((skill): skill is DeploymentSkillDocument => skill !== null),
    [deploymentSkills, selectedSkillDetails, selectedSkillIds],
  )

  const openSkillPreview = async (skillId: string) => {
    if (!orgId || !deploymentId) return

    const cached = selectedSkillDetails[skillId]
    if (cached) {
      setPreviewArtifact({
        title: cached.name || cached.id,
        body: cached.body || cached.content || t('chat.noSkillPreview'),
      })
      return
    }

    try {
      const detail = await skillsAPI.getDeploymentSkill(orgId, deploymentId, skillId)
      hydrateSelectedSkillDetail(detail)
      setPreviewArtifact({
        title: detail.name || detail.id,
        body: detail.body || detail.content || t('chat.noSkillPreview'),
      })
    } catch (loadError) {
      setPreviewArtifact({
        title: skillId,
        body: loadError instanceof Error ? loadError.message : t('chat.skillsLoadingError'),
      })
    }
  }

  const handleSkillToggle = async (skillId: string) => {
    if (!selectedSkillDetails[skillId] && orgId && deploymentId) {
      try {
        const detail = await skillsAPI.getDeploymentSkill(orgId, deploymentId, skillId)
        hydrateSelectedSkillDetail(detail)
      } catch {
        // Keep toggle behavior even if preview details fail to load.
      }
    }
    toggleSelectedSkillId(skillId)
  }

  const executionMessageContent =
    executionDetailTarget?.messageContent?.trim() ||
    messages.find(
      (message) =>
        message.metadata?.executionId === executionDetailTarget?.executionId,
    )?.content

  if (executionDetailTarget) {
    if (executionDetailTarget.mode === 'logs') {
      return (
        <RecipeExecutionLogsPanel
          execution={detailExecution}
          executionId={executionDetailTarget.executionId}
          recipeName={executionDetailTarget.recipeName}
          logs={executionDetailTarget.logs}
          expired={
            detailExpired || isExecutionPayloadExpired(detailExecution?.payload_state)
          }
          isLoading={detailLoading}
          isRefreshing={detailLoading}
          error={detailError}
          onRefresh={() => setDetailRefreshTick((value) => value + 1)}
          onClose={onCloseExecutionDetail}
        />
      )
    }

    return (
      <RecipeExecutionResultPanel
        execution={detailExecution}
        executionId={executionDetailTarget.executionId}
        recipeName={executionDetailTarget.recipeName}
        resultData={executionDetailTarget.resultData}
        messageContent={executionMessageContent}
        expired={
          detailExpired || isExecutionPayloadExpired(detailExecution?.payload_state)
        }
        isLoading={detailLoading}
        isRefreshing={detailLoading}
        error={detailError}
        onRefresh={() => setDetailRefreshTick((value) => value + 1)}
        onClose={onCloseExecutionDetail}
      />
    )
  }

  const hasSessionContext = Boolean(sessionId)

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {showExecutionPanel && (
        <ExecutionPanel
          isOpen={executionPanelOpen}
          onToggle={onToggleExecution}
          steps={executionSteps}
          totalDuration={totalDuration}
          currentProgress={currentProgress}
        />
      )}

      <div
        className={`min-h-0 flex-1 space-y-4 overflow-y-auto p-4 ${
          showExecutionPanel ? 'border-t border-border' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold">{t('runContext.title')}</h4>
            <p className="text-xs text-muted-foreground">
              {t('runContext.description')}
            </p>
          </div>
          {hasSessionContext && workspaceContext?.degraded && (
            <Badge variant="outline">{t('build.degraded')}</Badge>
          )}
        </div>

        {hasSessionContext && loading && !workspaceContext && (
          <div className={`${runMutedPanelClassName} flex items-center gap-2 text-sm text-muted-foreground`}>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('runContext.loadingContext')}
          </div>
        )}

        {hasSessionContext && error && !workspaceContext && (
          <div className="rounded-2xl border border-error/30 bg-error-soft px-3 py-2 text-sm text-error">
            {error}
          </div>
        )}

        {hasSessionContext && (
          <div className="grid grid-cols-2 gap-3">
            <SummaryCard icon={Clock3} label={t('build.openTasks')} value={counts.tasks} />
            <SummaryCard icon={Sparkles} label={t('build.timeline')} value={counts.timeline} />
            <SummaryCard icon={FileText} label={t('build.artifacts')} value={counts.artifacts} />
            <SummaryCard icon={Boxes} label={t('build.sourceGraph')} value={counts.sources} />
          </div>
        )}

        <section className={runInsetCardClassName}>
          <div className="flex items-center gap-2">
            <Workflow className="h-4 w-4 text-primary" />
            <div>
              <h4 className="text-sm font-semibold">{t('runContext.activeRecipe')}</h4>
              <p className="text-xs text-muted-foreground">
                {t('runContext.activeRecipeDescription')}
              </p>
            </div>
          </div>

          {recipeLoading ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('runContext.loadingRecipe')}
            </div>
          ) : recipe ? (
            <div className="mt-3 space-y-3">
              <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2">
                <div>
                  <div className="text-sm font-medium">{recipe.name}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {recipe.description || t('common.noDescription')}
                  </div>
                </div>
                <Badge variant="outline">v{recipe.version}</Badge>
              </div>
              <RecipePreviewCanvas recipe={recipe} />
            </div>
          ) : recipeError ? (
            <div className="rounded-xl border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
              {recipeError}
            </div>
          ) : (
            <div className={`${runMutedPanelClassName} mt-3 text-xs text-muted-foreground`}>
              {t('runContext.notLinked')}
            </div>
          )}
        </section>

        <section className={runInsetCardClassName}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <div>
              <h4 className="text-sm font-semibold">{t('chat.skillsPanelTitle')}</h4>
              <p className="text-xs text-muted-foreground">
                {t('chat.skillsPanelDescription')}
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {selectedSkills.length > 0 ? (
              selectedSkills.map((skill) => (
                <Badge key={skill.id} variant="secondary" className="gap-2 px-3 py-1">
                  <span>{skill.name || skill.id}</span>
                  <button
                    type="button"
                    className="text-muted-foreground transition hover:text-foreground"
                    onClick={() => void handleSkillToggle(skill.id)}
                  >
                    {t('common.remove')}
                  </button>
                </Badge>
              ))
            ) : (
              <div className={`${runMutedPanelClassName} w-full text-xs text-muted-foreground`}>
                {t('chat.noSelectedSkills')}
              </div>
            )}
          </div>

          <div className="mt-4 space-y-2">
            {skillsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('chat.skillsLoading')}
              </div>
            ) : skillsError ? (
              <div className="rounded-xl border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
                {skillsError}
              </div>
            ) : deploymentSkills.length === 0 ? (
              <div className={`${runMutedPanelClassName} text-xs text-muted-foreground`}>
                {t('chat.noDeploymentSkills')}
              </div>
            ) : (
              deploymentSkills.slice(0, 8).map((skill) => {
                const selected = selectedSkillIds.includes(skill.id)
                return (
                  <div
                    key={skill.id}
                    className="rounded-xl border border-border bg-card px-3 py-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{skill.name}</div>
                        {skill.description ? (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {skill.description}
                          </div>
                        ) : null}
                        <div className="mt-2 flex flex-wrap gap-2">
                          {skill.version ? <Badge variant="outline">v{skill.version}</Badge> : null}
                          {skill.mcpTools.length > 0 ? (
                            <Badge variant="outline">{skill.mcpTools.length} MCP</Badge>
                          ) : null}
                          {!skill.valid ? <Badge variant="outline">{t('common.error')}</Badge> : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          type="button"
                          variant={selected ? 'secondary' : 'outline'}
                          size="sm"
                          onClick={() => void handleSkillToggle(skill.id)}
                        >
                          {selected ? t('chat.skillSelected') : t('chat.useSkill')}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void openSkillPreview(skill.id)}
                        >
                          {t('common.open')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </section>

        {hasSessionContext && (
          <>
            <ContextList
              title={t('runContext.openTasks')}
              icon={Clock3}
              rows={workspaceContext?.openTasks || []}
              emptyLabel={t('runContext.noOpenTasks')}
              summaryKeys={['title', 'task_title', 'name', 'task_id']}
            />

            <ContextList
              title={t('runContext.timeline')}
              icon={Sparkles}
              rows={workspaceContext?.timeline || []}
              emptyLabel={t('runContext.noTimeline')}
              summaryKeys={['summary', 'title', 'event_type', 'kind']}
            />

            <ContextList
              title={t('runContext.artifacts')}
              icon={FileText}
              rows={workspaceContext?.recentArtifacts || []}
              emptyLabel={t('runContext.noArtifacts')}
              summaryKeys={['name', 'artifact_id', 'path', 'storage_uri']}
              onPreviewRow={openArtifactPreview}
            />

            <ContextList
              title={t('runContext.sourceGraph')}
              icon={Boxes}
              rows={workspaceContext?.sourceGraph || []}
              emptyLabel={t('runContext.noSources')}
              summaryKeys={['title', 'name', 'kind', 'node_id']}
            />
          </>
        )}
      </div>

      <Dialog open={Boolean(previewArtifact)} onOpenChange={(open) => !open && setPreviewArtifact(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{previewArtifact?.title || t('runContext.previewTitle')}</DialogTitle>
            <DialogDescription>
              {t('runContext.previewDescription')}
            </DialogDescription>
          </DialogHeader>
          {previewArtifact?.link && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.open(previewArtifact.link || '', '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('runContext.openFile')}
              </Button>
            </div>
          )}
          <pre className={`${runMutedPanelClassName} max-h-[60vh] overflow-auto whitespace-pre-wrap text-xs`}>
            {previewArtifact?.body || t('runContext.noPreview')}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  )
}
